import { useEffect, useState, useCallback, useRef } from 'react';

const CHANNEL_NAME = 'space-food-chat-tab';
const RECHECK_TIMEOUT_MS = 600;

type MensagemTipo = 'ANUNCIO' | 'JA_ATIVO' | 'ASSUMIR' | 'FECHAR';

interface MensagemCanal {
    tipo: MensagemTipo;
    tabId: string;
}

export function useSingleTabChat() {
    const tabId = useRef(`tab-${Date.now()}-${Math.random()}`);
    const channelRef = useRef<BroadcastChannel | null>(null);
    const isActiveRef = useRef(false);
    const recheckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [chatBloqueado, setChatBloqueado] = useState(false);
    const [motivo, setMotivo] = useState<'outra-aba-ativa' | 'assumida-por-outra' | null>(null);

    const assumirControle = useCallback(() => {
        channelRef.current?.postMessage({ tipo: 'ASSUMIR', tabId: tabId.current } as MensagemCanal);
        isActiveRef.current = true;
        setChatBloqueado(false);
        setMotivo(null);
    }, []);

    useEffect(() => {
        if (typeof BroadcastChannel === 'undefined') {
            isActiveRef.current = true;
            return;
        }

        const channel = new BroadcastChannel(CHANNEL_NAME);
        channelRef.current = channel;

        const setActive = () => {
            isActiveRef.current = true;
            setChatBloqueado(false);
            setMotivo(null);
        };

        const setBlocked = (m: 'outra-aba-ativa' | 'assumida-por-outra') => {
            isActiveRef.current = false;
            setChatBloqueado(true);
            setMotivo(m);
        };

        const anunciar = () => {
            channel.postMessage({ tipo: 'ANUNCIO', tabId: tabId.current } as MensagemCanal);

            if (recheckTimerRef.current) clearTimeout(recheckTimerRef.current);
            recheckTimerRef.current = setTimeout(() => {
                if (!isActiveRef.current) {
                    setActive();
                }
                recheckTimerRef.current = null;
            }, RECHECK_TIMEOUT_MS);
        };

        channel.onmessage = (event: MessageEvent<MensagemCanal>) => {
            const { data } = event;
            if (data.tabId === tabId.current) return;

            switch (data.tipo) {
                case 'ANUNCIO':
                    if (isActiveRef.current) {
                        channel.postMessage({ tipo: 'JA_ATIVO', tabId: tabId.current } as MensagemCanal);
                    }
                    break;

                case 'JA_ATIVO':
                    if (recheckTimerRef.current) {
                        clearTimeout(recheckTimerRef.current);
                        recheckTimerRef.current = null;
                    }
                    setBlocked('outra-aba-ativa');
                    break;

                case 'ASSUMIR':
                    if (isActiveRef.current) {
                        setBlocked('assumida-por-outra');
                    }
                    break;

                case 'FECHAR':
                    if (!isActiveRef.current) {
                        anunciar();
                    }
                    break;
            }
        };

        anunciar();

        return () => {
            if (isActiveRef.current) {
                channel.postMessage({ tipo: 'FECHAR', tabId: tabId.current } as MensagemCanal);
            }
            if (recheckTimerRef.current) clearTimeout(recheckTimerRef.current);
            channel.close();
            channelRef.current = null;
        };
    }, []);

    return { chatBloqueado, motivo, assumirControle };
}
